import { type LineItem } from "../models/CartData";
import { userIsSalesman } from "~/domains/user/utils/roles";
import * as QuoteUpdateApi from "~/features/cart/activeQuote/api/updateQuoteEntry";
import * as CartUpdateApi from "./UpdatePackageEntry";
import * as CartDeleteApi from "./DeletePackageEntry";
import * as CartLoadApi from "./CartView";
import * as QuoteLoadApi from "~/features/cart/activeQuote/api/quoteView";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { TransformedCartData } from "../transformers/CartResponseTransformer";
import { type AddToCartItem, addToCart } from "~/features/addToCart/redux/addToCart/productAddFlow";

export function sendDeleteEntry(entry: LineItem): Promise<unknown> {
  entry = { ...entry, quantity: 0 };
  if (userIsSalesman()) {
    const apiItem = QuoteUpdateApi.fromLineItem(entry);
    return QuoteUpdateApi.sendUpdate(apiItem);
  } else {
    const apiItem = CartUpdateApi.fromLineItem(entry, []);
    return CartDeleteApi.sendDelete(apiItem);
  }
}

export function sendBulkEditEntry(items: LineItem[], subLineItemsByParentId: Dict<LineItem[]>): Promise<unknown> {
  if (userIsSalesman()) {
    const apiItems = items.map((item) => QuoteUpdateApi.fromLineItem(item));
    return QuoteUpdateApi.sendBulkUpdate(apiItems);
  } else {
    const apiItems = items.map((item) => CartUpdateApi.fromLineItem(item, subLineItemsByParentId[item.id] ?? []));
    return CartUpdateApi.sendBulkUpdate(apiItems);
  }
}

export async function sendAddEntry(item: LineItem, descriptor: ProductDescriptor): Promise<number> {
  const responseItems = await addToCart({
    items: [flowItemFromLineItem(item, descriptor)],
    page: "CART",
    source: "unknown",
    successUi: "none",
    skipCartContentTracking: true,
  });

  return responseItems[0].pk as number;
}

function flowItemFromLineItem(item: LineItem, descriptor: ProductDescriptor): AddToCartItem {
  const [quoteLine, quoteScheduleLineNum] = item.quoteLine.split(":");

  return {
    productCode: descriptor.base.code,
    packageSize: item.packSize,
    packageCode: item.packCode,
    quantity: item.quantity,
    createNewPackageEntry: true,
    comment: item.comment,
    selectedDeliveryDate: item.deliveryDate,
    selectedDeliveryDateIsManual: item.manuallySetRDD,
    primaryWarehouseCode: item.primaryWarehouseCode,
    contractNumber: item.contractNumber,
    contractLine: item.contractLine,
    priceReference: item.priceReference,
    quoteLine,
    quoteNumber: item.quoteNumber,
    quoteScheduleLineNum,
    quoteMinQuantity: item.minimumQty,
    isBundled: item.isBundled,
    quoteExpiryDate: item.quoteExpiryDate,
    logisticalComment: item.logisticalComment,
    estimatedShippingDate: item.estimatedShippingDate,
    restrictedPackage: item.restrictedPackage,
    customerOrderNumber: item.customerOrderNumber,
    originCountry: item.countryOfOrigin,
    salesCampaignCode: item.saleCampaignCode,
    offerPrice: item.offerPrice?.value,
    targetPrice: item.targetPrice?.value,
    msp: item.msp?.value,
    rsp: item.rsp?.value,
    internalComment: item.internalComment,
  };
}

export async function loadCart(): Promise<TransformedCartData | null> {
  if (userIsSalesman()) {
    return QuoteLoadApi.getData();
  } else if (userHasPermission(Permission.ADD_TO_CART)) {
    return CartLoadApi.getActiveCartData();
  }
  return Promise.resolve(null);
}
